// src/pages/stock/StockCreate.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const EntrerStockCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  
  // États du formulaire
  const [formData, setFormData] = useState({
    id_produit: "",
    quantite_entrer_stock: ""
  });
  
  // États pour l'UI
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  
  // États pour l'erreur d'abonnement
  const [abonnementError, setAbonnementError] = useState(false);
  const [abonnementMessage, setAbonnementMessage] = useState("");
  
  // État pour les produits récupérés depuis l'API
  const [produits, setProduits] = useState([]);
  // État pour les produits filtrés (pour la recherche)
  const [filteredProduits, setFilteredProduits] = useState([]);
  // État pour la recherche de produit
  const [produitSearch, setProduitSearch] = useState("");
  // État pour contrôler l'affichage de la liste déroulante
  const [showProduitDropdown, setShowProduitDropdown] = useState(false);
  
  // Vérifier si l'utilisateur a le bon profil (admin ou gestionnaire)
  const [hasPermission, setHasPermission] = useState(true);
  
  // Fonction pour réinitialiser l'erreur d'abonnement
  const resetAbonnementError = () => {
    setAbonnementError(false);
    setAbonnementMessage("");
  };
  
  // Fonction pour charger les produits actifs depuis l'API
  const loadProduits = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Token d'authentification manquant.");
        setLoadingProducts(false);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/stock/produits/list_produit.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log("Produits chargés:", response.data);
      
      if (response.data?.success && response.data?.data?.produits) {
        // Filtrer seulement les produits actifs (statut = 1)
        const produitsActifs = response.data.data.produits
          .filter(produit => parseInt(produit.statut) === 1)
          .map(produit => ({
            value: produit.id.toString(),
            label: `${produit.libele} (Stock: ${produit.quantite_stock})`,
            libele: produit.libele,
            stock: parseInt(produit.quantite_stock),
            id: produit.id
          }));
        
        setProduits(produitsActifs);
        setFilteredProduits(produitsActifs);
        
        // VÉRIFIER SI UN PRODUIT EST PASSÉ EN PARAMÈTRE D'URL
        const searchParams = new URLSearchParams(location.search);
        const produitId = searchParams.get('produit');
        
        if (produitId && produitsActifs.length > 0) {
          const produitTrouve = produitsActifs.find(p => p.id.toString() === produitId || p.value === produitId);
          if (produitTrouve) {
            // Pré-remplir le formulaire avec le produit trouvé
            setFormData(prev => ({
              ...prev,
              id_produit: produitTrouve.value,
            }));
            setProduitSearch(produitTrouve.libele);
          } else {
            setServerError(`Le produit sélectionné (ID: ${produitId}) n'est pas disponible dans la liste des produits actifs.`);
          }
        }
        
        if (produitsActifs.length === 0) {
          setServerError("Aucun produit actif disponible pour ajouter du stock.");
        }
      } else {
        setServerError("Erreur lors du chargement des produits.");
      }
    } catch (err) {
      console.error("Erreur chargement produits:", err);
      
      // Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setProduits([]);
        setFilteredProduits([]);
      } else {
        // Gestion des erreurs spécifiques
        if (err.response?.status === 401) {
          setServerError("Session expirée. Veuillez vous reconnecter.");
          setTimeout(() => navigate("/login"), 2000);
        } else if (err.response?.status === 403) {
          setServerError("Permission refusée pour accéder aux produits.");
        } else if (err.response?.status === 500) {
          setServerError("Erreur serveur lors du chargement des produits.");
        } else {
          setServerError("Erreur réseau lors du chargement des produits.");
        }
      }
    } finally {
      setLoadingProducts(false);
    }
  };
  
  useEffect(() => {
    // Vérifier le profil utilisateur
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userProfile = payload.profile;
        
        // Seuls les profils 1 (admin) et 3 (gestionnaire) peuvent créer des entrées
        if (![1, 3].includes(userProfile)) {
          setHasPermission(false);
          setServerError("Permission refusée. Seuls les administrateurs et gestionnaires peuvent créer une entrée de stock.");
        } else {
          // Si l'utilisateur a la permission, charger les produits
          loadProduits();
        }
      } catch (err) {
        console.error("Erreur décodage token:", err);
        setLoadingProducts(false);
      }
    } else {
      setServerError("Non authentifié. Veuillez vous connecter.");
      setLoadingProducts(false);
    }
  }, []);
  
  // Effet pour filtrer les produits quand la recherche change
  useEffect(() => {
    if (!produitSearch.trim()) {
      setFilteredProduits(produits);
    } else {
      const searchTerm = produitSearch.toLowerCase();
      const filtered = produits.filter(produit => 
        produit.libele.toLowerCase().includes(searchTerm) ||
        produit.label.toLowerCase().includes(searchTerm)
      );
      setFilteredProduits(filtered);
    }
  }, [produitSearch, produits]);
  
  // Fermer la dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProduitDropdown(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  
  // Obtenir le produit sélectionné pour afficher le stock actuel
  const selectedProduit = produits.find(p => p.value === formData.id_produit);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Nettoyer les valeurs selon le champ
    let cleanedValue = value;
    
    if (name === "quantite_entrer_stock") {
      // Ne permettre que les nombres positifs
      cleanedValue = value.replace(/\D/g, "");
      
      // Limiter à un nombre raisonnable
      const numValue = parseInt(cleanedValue);
      if (numValue > 999999) {
        cleanedValue = "999999";
      }
    }
    
    setFormData({
      ...formData,
      [name]: cleanedValue
    });
    
    // Effacer les erreurs de validation quand l'utilisateur modifie
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };
  
  // Gérer la sélection d'un produit
  const handleProduitSelect = (produitValue, produitLabel) => {
    setFormData({
      ...formData,
      id_produit: produitValue
    });
    setProduitSearch(produits.find(p => p.value === produitValue)?.libele || produitLabel);
    setShowProduitDropdown(false);
  };
  
  // Gérer la recherche de produit
  const handleProduitSearchChange = (e) => {
    const value = e.target.value;
    setProduitSearch(value);
    
    // Si le champ est vide, réinitialiser la sélection
    if (!value.trim()) {
      setFormData({
        ...formData,
        id_produit: ""
      });
    }
    
    // Ouvrir la dropdown quand on commence à taper
    if (value && !showProduitDropdown) {
      setShowProduitDropdown(true);
    }
  };
  
  const validateForm = () => {
    const errors = [];
    
    // Validation du produit
    if (!formData.id_produit || formData.id_produit === "") {
      errors.push("La sélection d'un produit est obligatoire.");
    }
    
    // Validation de la quantité
    const quantite = parseInt(formData.quantite_entrer_stock);
    if (isNaN(quantite) || quantite <= 0) {
      errors.push("La quantité doit être supérieure à 0.");
    }
    
    return errors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Réinitialiser les messages
    setServerError("");
    setValidationErrors([]);
    resetAbonnementError();
    
    // Valider le formulaire
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setServerError("Veuillez corriger les erreurs dans le formulaire.");
      return;
    }
    
    // Préparer les données pour l'API
    const submitData = {
      id_produit: parseInt(formData.id_produit),
      quantite_entrer_stock: parseInt(formData.quantite_entrer_stock)
    };
    
    setLoading(true);
    
    try {
      const url = `${API_BASE_URL}/stock/entrer_stock/create_entrer.php`;
      console.log("Envoi des données:", submitData);
      
      const response = await axios.post(url, submitData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
      });
      
      console.log("Réponse création entrée stock:", response.data);
      
      if (response.data?.success) {
        setInfoMsg(response.data.message || "Entrée de stock ajoutée avec succès !");
        
        // Rediriger vers la liste des produits après 1 secondes
        setTimeout(() => {
          navigate("/products");
        }, 1000);
        
        // Afficher les détails de l'entrée créée
        if (response.data.entree_stock) {
          console.log("Entrée créée:", response.data.entree_stock);
        }
      } else {
        setServerError(
          response.data?.message ||
          "Impossible de créer l'entrée de stock. Veuillez réessayer."
        );
        
        // Gérer les erreurs de validation retournées par l'API
        if (response.data?.errors) {
          setValidationErrors(response.data.errors);
        }
      }
    } catch (err) {
      console.error("Erreur création entrée stock:", err);
      
      // 🔍 Vérifier si c'est une erreur 403 avec code ABONNEMENT_REQUIS
      if (err.response?.status === 403 && err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        setAbonnementError(true);
        setAbonnementMessage(err.response.data.message || "Abonnement inactif");
        setLoading(false);
        return;
      }
      
      // Gestion des erreurs HTTP spécifiques
      if (err.response?.status === 400) {
        // Erreurs de validation
        if (err.response.data?.errors) {
          setValidationErrors(err.response.data.errors);
          setServerError("Erreurs de validation");
        } else {
          setServerError(err.response.data?.message || "Données invalides.");
        }
      } else if (err.response?.status === 403) {
        // Permission refusée
        setHasPermission(false);
        setServerError(err.response.data?.message || "Permission refusée.");
      } else if (err.response?.status === 404) {
        setServerError("Produit introuvable.");
      } else if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 500) {
        setServerError("Erreur serveur. Veuillez réessayer plus tard.");
      } else {
        setServerError("Erreur réseau. Vérifiez votre connexion.");
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    if (window.confirm("Voulez-vous annuler la création ? Les modifications seront perdues.")) {
      navigate("/products");
    }
  };
  
  // Si l'utilisateur n'a pas la permission, afficher un message d'erreur
  if (!hasPermission) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion de stocks</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</a>
              </li>
              <li className="breadcrumb-item">
                <a href="/products" style={{ color: COLORS.primary }}>Produits</a>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
                Nouvelle entrée de stock
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="alert alert-dismissible fade show" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`,
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-shield-exclamation me-2 fs-4"></i>
                  <div className="flex-grow-1">{serverError}</div>
                </div>
              </div>
              <button
                className="btn fw-semibold mt-4"
                onClick={() => navigate("/products")}
                style={{
                  color: COLORS.white,
                  backgroundColor: COLORS.primary,
                  borderColor: COLORS.primary,
                  borderRadius: '8px',
                  padding: '10px 24px',
                  minWidth: '200px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#0A3250';
                  e.target.style.borderColor = '#0A3250';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.borderColor = COLORS.primary;
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour à la liste
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion de stocks</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</a>
            </li>
            <li className="breadcrumb-item">
              <a href="/products" style={{ color: COLORS.primary }}>Produits</a>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
              Nouvelle entrée de stock
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h5 className="card-title mb-1" style={{ 
                      color: COLORS.primary,
                      paddingBottom: '16px',
                      width: '100%'
                    }}>
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                          width: '36px',
                          height: '36px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.success
                        }}>
                          <i className="bi bi-plus-circle"></i>
                        </div>
                        Nouvelle entrée de stock
                      </div>
                    </h5>
                  </div>
             </div>
          </div>

            {/* Affichage de l'erreur d'abonnement */}
            {abonnementError && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#ff8f00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`,
                whiteSpace: "pre-line"
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    {abonnementMessage}
                    <div className="d-flex gap-2 justify-content-center mt-3">
                      <a 
                        href="/abonnements/create"
                        className="btn fw-semibold"
                        style={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                        Souscrire à un abonnement
                      </a>
                      <button
                        className="btn fw-semibold"
                        onClick={() => {
                          setAbonnementError(false);
                          setAbonnementMessage("");
                        }}
                        style={{
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 20px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                      >
                        Fermer
                      </button>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setAbonnementError(false);
                      setAbonnementMessage("");
                    }}
                  ></button>
                </div>
              </div>
            )}

            {/* Messages d'erreur/succès */}
            {serverError && !abonnementError && !validationErrors.length && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {validationErrors.length > 0 && (
              <div className="alert alert-warning alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#fff3e0',
                borderColor: COLORS.warning,
                color: '#f57c00',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.warning}`
              }}>
                <div className="d-flex align-items-start">
                  <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
                  <div className="flex-grow-1">
                    <h6 className="alert-heading mb-2" style={{ color: '#e65100' }}>
                      Erreurs de validation
                    </h6>
                    <ul className="mb-0 ps-3" style={{ fontSize: '0.9rem' }}>
                      {validationErrors.map((error, index) => (
                        <li key={index} style={{ marginBottom: '4px' }}>{error}</li>
                      ))}
                    </ul>
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setValidationErrors([])}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle me-2"></i>
                  <div className="flex-grow-1">
                    {infoMsg}
                    <div className="mt-2 small" style={{ color: '#1b5e20' }}>
                      <div className="spinner-border spinner-border-sm text-success me-2" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Redirection vers la liste des produits...
                    </div>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Sélection du produit avec recherche - col-12 */}
                <div className="col-12 mb-4" ref={dropdownRef}>
                  <label className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                  <span className="text-danger">*</span> Produit :
                  </label>
                  
                  <div className="position-relative">
                    {/* Champ de recherche */}
                    <div className="input-group">
                      <span className="input-group-text" style={{
                        backgroundColor: COLORS.lightBg,
                        borderColor: '#e0e0e0',
                        color: COLORS.secondary
                      }}>
                        <i className="bi bi-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        id="produit-search"
                        placeholder="Rechercher un produit..."
                        value={produitSearch}
                        onChange={handleProduitSearchChange}
                        onFocus={() => setShowProduitDropdown(true)}
                        disabled={loading || loadingProducts || produits.length === 0}
                        required
                        style={{
                          borderColor: '#ced4da',
                          padding: '10px 12px',
                          fontSize: '0.95rem',
                          backgroundColor: COLORS.white,
                          transition: 'all 0.3s ease'
                        }}
                      />
                      {formData.id_produit && (
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => {
                            setFormData({ ...formData, id_produit: "" });
                            setProduitSearch("");
                          }}
                          title="Effacer la sélection"
                          disabled={loading || loadingProducts}
                        >
                          <i className="bi bi-x"></i>
                        </button>
                      )}
                    </div>
                    
                    {/* Dropdown des résultats - sans bordure */}
                    {showProduitDropdown && !loadingProducts && produits.length > 0 && (
                      <div className="dropdown-menu show w-100" style={{ 
                        maxHeight: "250px", 
                        overflowY: "auto",
                        zIndex: 1050,
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        border: 'none'
                      }}>
                        {filteredProduits.length > 0 ? (
                          filteredProduits.map((produit) => (
                            <button
                              key={produit.value}
                              type="button"
                              className={`dropdown-item d-flex justify-content-between align-items-center ${formData.id_produit === produit.value ? 'bg-primary text-white' : ''}`}
                              onClick={() => handleProduitSelect(produit.value, produit.label)}
                              style={{
                                padding: '10px 16px'
                              }}
                            >
                              <div>
                                <div className="fw-bold" style={{ fontSize: "0.95rem" }}>{produit.libele}</div>
                                <small className={formData.id_produit === produit.value ? 'text-light' : 'text-muted'} style={{ fontSize: "0.8rem" }}>
                                  Stock actuel: {produit.stock}
                                </small>
                              </div>
                              {formData.id_produit === produit.value && (
                                <i className="bi bi-check-lg"></i>
                              )}
                            </button>
                          ))
                        ) : (
                          <div className="dropdown-item text-muted text-center py-3">
                            <i className="bi bi-search me-2"></i>
                            Aucun produit trouvé
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Badge pour la sélection actuelle */}
                    {formData.id_produit && produits.length > 0 && (
                      <div className="mt-3">
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '0.85rem'
                        }}>
                          <i className="bi bi-check-circle me-1"></i>
                          Sélectionné: {produits.find(p => p.value === formData.id_produit)?.libele}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {loadingProducts && (
                    <div className="form-text mt-2 d-flex align-items-center" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      <div className="spinner-border spinner-border-sm me-2" role="status" style={{ color: COLORS.primary }}>
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                      Chargement des produits...
                    </div>
                  )}
                  
                  {produits.length === 0 && !loadingProducts && !abonnementError && (
                    <div className="form-text d-flex align-items-center mt-2" style={{ color: COLORS.warning, fontSize: '0.85rem' }}>
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Aucun produit actif disponible. Activez d'abord un produit.
                    </div>
                  )}
                  
                  {/* Information sur le nombre de produits */}
                  {produits.length > 0 && !loadingProducts && (
                    <div className="form-text mt-2 d-flex align-items-center" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                      <i className="bi bi-info-circle me-2"></i>
                      <span>
                        {produits.length} produit{produits.length > 1 ? 's' : ''} disponible{produits.length > 1 ? 's' : ''}
                        {produitSearch && filteredProduits.length > 0 && (
                          <span className="ms-2">
                            ({filteredProduits.length} résultat{filteredProduits.length > 1 ? 's' : ''})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  
                  {/* Affichage du stock actuel du produit sélectionné */}
                  {selectedProduit && (
                    <div className="alert alert-dismissible fade show mt-3" style={{ 
                      backgroundColor: '#E3F2FD',
                      borderColor: COLORS.info,
                      color: COLORS.primary,
                      borderRadius: '8px',
                      borderLeft: `4px solid ${COLORS.info}`,
                      padding: '12px 16px'
                    }}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-box-seam me-2 fs-5"></i>
                        <div>
                          <strong>Stock actuel:</strong>{" "}
                          <span className="badge fw-semibold ms-2" style={{ 
                            backgroundColor: COLORS.info,
                            color: COLORS.white,
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '0.85rem'
                          }}>
                            {selectedProduit.stock} unités
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Quantité à ajouter - col-12 */}
                <div className="col-12 mb-4">
                  <label htmlFor="quantite_entrer_stock" className="form-label fw-bold mb-3" style={{ 
                    color: COLORS.darkText,
                    fontSize: '0.95rem'
                  }}>
                  <span className="text-danger">*</span> Quantité à ajouter :
                  </label>
                  <div className="input-group">
                    <span className="input-group-text" style={{
                      backgroundColor: COLORS.lightBg,
                      borderColor: '#e0e0e0',
                      color: COLORS.success
                    }}>
                      <i className="bi bi-plus-lg"></i>
                    </span>
                    <input
                      type="number"
                      className="form-control"
                      id="quantite_entrer_stock"
                      name="quantite_entrer_stock"
                      value={formData.quantite_entrer_stock}
                      onChange={handleChange}
                      placeholder="Ex: 50"
                      disabled={loading || loadingProducts || produits.length === 0 || !formData.id_produit}
                      required
                      style={{
                        borderColor: '#ced4da',
                        borderRadius: '0 6px 6px 0',
                        padding: '10px 12px',
                        fontSize: '0.95rem',
                        backgroundColor: COLORS.white,
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = COLORS.secondary;
                        e.target.style.boxShadow = `0 0 0 0.2rem rgba(30, 136, 200, 0.1)`;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#ced4da';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  <div className="form-text mt-2" style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Entrez la quantité à ajouter au stock existant.
                  </div>
                  {selectedProduit && formData.quantite_entrer_stock && (
                    <div className="alert alert-dismissible fade show mt-3" style={{ 
                      backgroundColor: '#e8f5e9',
                      borderColor: COLORS.success,
                      color: '#2e7d32',
                      borderRadius: '8px',
                      borderLeft: `4px solid ${COLORS.success}`,
                      padding: '12px 16px'
                    }}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-calculator me-2 fs-5"></i>
                        <div>
                          <strong>Nouveau stock après ajout:</strong>{" "}
                          <span className="ms-2">
                            {selectedProduit.stock} + {formData.quantite_entrer_stock} ={" "}
                            <strong className="text-success" style={{ fontSize: '1.1rem' }}>
                              {selectedProduit.stock + parseInt(formData.quantite_entrer_stock || 0)}
                            </strong> unités
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="mt-5 pt-4 border-top" style={{ borderColor: '#e0e0e0' }}>
                <div className="d-flex justify-content-end gap-3">
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleCancel}
                    disabled={loading || loadingProducts}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      minWidth: '120px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && !loadingProducts) {
                        e.target.style.backgroundColor = '#E3F2FD';
                        e.target.style.borderColor = COLORS.secondary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading && !loadingProducts) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                  >
                    <i className="bi bi-x-lg me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    type="submit"
                    className="btn fw-semibold"
                    disabled={loading || loadingProducts || produits.length === 0 || !formData.id_produit}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: '8px',
                      padding: '10px 24px',
                      minWidth: '180px'
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Ajout en cours...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-plus-lg me-2 fw-bold"></i>
                        Ajouter au stock
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .btn:focus,
        .form-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .form-control.is-invalid {
          border-color: ${COLORS.danger};
          background-color: #fff5f5;
        }
        
        .form-control.is-invalid:focus {
          box-shadow: 0 0 0 0.2rem rgba(244, 67, 54, 0.25);
        }
        
        .breadcrumb-item a {
          text-decoration: none;
          transition: color 0.3s ease;
        }
        
        .breadcrumb-item a:hover {
          color: ${COLORS.secondary} !important;
        }
        
        .badge.bg-info {
          background-color: ${COLORS.info} !important;
        }
        
        .badge.bg-danger {
          background-color: ${COLORS.danger} !important;
        }
        
        .dropdown-item.active, 
        .dropdown-item:active {
          background-color: ${COLORS.primary} !important;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-end {
            flex-direction: column;
            gap: 12px;
          }
          
          .btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </Layout>
  );
};

export default EntrerStockCreate;